import { useTranslation } from "react-i18next";
import { Eye, Zap, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CreditCostBadge } from "./CreditCostBadge";
import { ModelLockBadge } from "./ModelLockBadge";
import type { ModelEntry } from "@/lib/credits/api";

type Props = {
  model: ModelEntry;
  selected: boolean;
  onClick?: () => void;
  isDefault?: boolean;
  balance?: number | null;
  inputId?: string;
};

const providerEmoji: Record<string, string> = {
  anthropic: "🟧",
  openai: "🟦",
  google: "🟩",
  flux: "🟪",
  unsplash: "📸",
  pexels: "📷",
  voyage: "🟫",
};

export function ModelCard({ model, selected, onClick, isDefault, balance, inputId }: Props) {
  const { t } = useTranslation();
  const locked = model.locked;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={locked}
      onClick={locked ? undefined : onClick}
      id={inputId}
      className={cn(
        "text-left w-full",
        "transition-all",
        locked ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <Card
        className={cn(
          "p-3 border-2",
          selected && !locked && "border-primary ring-2 ring-primary/20",
          !selected && !locked && "border-border hover:border-primary/40",
          locked && "opacity-60 border-dashed",
        )}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <span aria-hidden className="text-base">{providerEmoji[model.provider] ?? "⚙️"}</span>
            <div className="font-medium text-sm truncate">{model.label}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isDefault && (
              <Badge variant="default" className="text-[10px] gap-1">
                <Star className="h-3 w-3" />
                {t("models.recommended")}
              </Badge>
            )}
          </div>
        </div>
        {model.description && (
          <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{model.description}</div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <CreditCostBadge cost={model.credits_cost} balance={balance ?? null} inline />
          {model.quality_tier && (
            <Badge variant="secondary" className="text-[10px]">
              {t(`models.quality${model.quality_tier.charAt(0).toUpperCase() + model.quality_tier.slice(1)}`)}
            </Badge>
          )}
          {model.speed_tier && (
            <Badge variant="outline" className="text-[10px] flex items-center gap-0.5">
              <Zap className="h-3 w-3" />
              {t(`models.speed${model.speed_tier.charAt(0).toUpperCase() + model.speed_tier.slice(1)}`)}
            </Badge>
          )}
          {model.supports_vision && (
            <Badge variant="outline" className="text-[10px] flex items-center gap-0.5">
              <Eye className="h-3 w-3" />
              {t("models.supportsVision")}
            </Badge>
          )}
          {locked && (
            <ModelLockBadge requiredTier={model.required_package_name ?? null} upgradeUrl={model.upgrade_url} />
          )}
        </div>
      </Card>
    </button>
  );
}

export default ModelCard;
